import { useEffect, useState } from 'react'

/**
 * Hook to detect if an element has a visible scrollbar
 * @param elementRef - Ref to the element to monitor
 * @returns boolean indicating if scrollbar is visible
 */
export function useHasScrollbar(elementRef: React.RefObject<HTMLElement | null>) {
  // Force a second render to ensure that there's always at least one render with the ref set.
  const [isFirstRender, setIsFirstRender] = useState(true)
  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false)
    }
  }, [isFirstRender])

  const [hasScrollbar, setHasScrollbar] = useState(false)

  useEffect(() => {
    const element = elementRef.current
    if (!element) {
      return
    }

    const checkScrollbar = () => {
      const hasVerticalScrollbar = element.scrollHeight > element.clientHeight
      setHasScrollbar(hasVerticalScrollbar)
    }

    // Check initially
    checkScrollbar()

    // Create a ResizeObserver to monitor size changes
    const resizeObserver = new ResizeObserver(checkScrollbar)
    resizeObserver.observe(element)

    // Also listen for content changes that might affect scrollability.
    // Omit attributes/characterData: attribute mutations inside a Radix Dialog (focus, ARIA, data-state)
    // fire synchronously during React's commit phase and trigger setState loops.
    const mutationObserver = new MutationObserver(checkScrollbar)
    mutationObserver.observe(element, {
      childList: true,
      subtree: true,
    })

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [elementRef.current])

  return hasScrollbar
}
